import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { TagRecord, TaggedEntity } from './taxonomy.types.js'

const ASSIGNMENTS_TABLE = 'tag_assignments'
const TAGS_TABLE = 'tags'

export type TaxonomyService = ReturnType<typeof createTaxonomyService>

export function createTaxonomyService(ctx: ModuleContext) {
  const db = ctx.db.knex

  function validateEntityType(entityType: string): void {
    if (!entityType || !entityType.includes('.')) {
      throw new (ctx.core.errors as any).ValidationError('entityType must be "module.entity" format')
    }
  }

  async function checkEntityAccess(entityType: string, entityId: string, auth: { userId?: string; ability?: any }): Promise<void> {
    const responses = await ctx.events.query<boolean>('taxonomy.checkEntityAccess', {
      entityType, entityId, userId: auth.userId, ability: auth.ability
    })
    if (!responses.some(r => r === true)) {
      throw new (ctx.core.errors as any).ForbiddenError('Cannot access target entity')
    }
  }

  async function assign(tagId: string, entityType: string, entityId: string): Promise<void> {
    const existing = await db(ASSIGNMENTS_TABLE)
      .where({ tag_id: tagId, entity_type: entityType, entity_id: entityId })
      .first()
    if (existing) return // idempotent
    const id = ctx.core.generateId()
    await db(ASSIGNMENTS_TABLE).insert({
      id,
      tag_id: tagId,
      entity_type: entityType,
      entity_id: entityId,
      created_at: ctx.db.nowTimestamp(db)
    })
  }

  async function unassign(tagId: string, entityType: string, entityId: string): Promise<void> {
    await db(ASSIGNMENTS_TABLE)
      .where({ tag_id: tagId, entity_type: entityType, entity_id: entityId })
      .delete()
  }

  async function syncTags(entityType: string, entityId: string, tagIds: string[]): Promise<void> {
    const existing = await db(ASSIGNMENTS_TABLE)
      .where({ entity_type: entityType, entity_id: entityId })
      .select('tag_id')

    const existingIds = new Set(existing.map((r: { tag_id: string }) => r.tag_id))
    const desiredIds = new Set(tagIds)

    const toRemove = [...existingIds].filter(id => !desiredIds.has(id))
    if (toRemove.length > 0) {
      await db(ASSIGNMENTS_TABLE)
        .where({ entity_type: entityType, entity_id: entityId })
        .whereIn('tag_id', toRemove)
        .delete()
    }

    const toAdd = tagIds.filter(id => !existingIds.has(id))
    if (toAdd.length > 0) {
      const rows = toAdd.map(tagId => ({
        id: ctx.core.generateId(),
        tag_id: tagId,
        entity_type: entityType,
        entity_id: entityId,
        created_at: ctx.db.nowTimestamp(db)
      }))
      await db(ASSIGNMENTS_TABLE).insert(rows)
    }
  }

  async function removeAllForEntity(entityType: string, entityId: string): Promise<void> {
    await db(ASSIGNMENTS_TABLE)
      .where({ entity_type: entityType, entity_id: entityId })
      .delete()
  }

  async function getTagsForEntity(entityType: string, entityId: string): Promise<TagRecord[]> {
    return db(ASSIGNMENTS_TABLE)
      .where({ entity_type: entityType, entity_id: entityId })
      .join(TAGS_TABLE, `${TAGS_TABLE}.id`, `${ASSIGNMENTS_TABLE}.tag_id`)
      .select(
        `${TAGS_TABLE}.id`,
        `${TAGS_TABLE}.name`,
        `${TAGS_TABLE}.slug`,
        `${TAGS_TABLE}.description`,
        `${TAGS_TABLE}.color`,
        `${TAGS_TABLE}.icon`
      )
  }

  async function findBySlug(slug: string): Promise<TagRecord | null> {
    return db(TAGS_TABLE).where('slug', slug).first() ?? null
  }

  async function getEntitiesForTag(tagId: string, entityType?: string): Promise<TaggedEntity[]> {
    const query = db(ASSIGNMENTS_TABLE)
      .where({ tag_id: tagId })
    if (entityType) {
      query.andWhere('entity_type', entityType)
    }
    return query.select('entity_type', 'entity_id')
  }

  async function getTagsForEntities(entityType: string, entityIds: string[]): Promise<Map<string, TagRecord[]>> {
    const result = new Map<string, TagRecord[]>()
    if (entityIds.length === 0) return result

    const rows = await db(ASSIGNMENTS_TABLE)
      .whereIn('entity_id', entityIds)
      .andWhere('entity_type', entityType)
      .join(TAGS_TABLE, `${TAGS_TABLE}.id`, `${ASSIGNMENTS_TABLE}.tag_id`)
      .select(
        `${ASSIGNMENTS_TABLE}.entity_id`,
        `${TAGS_TABLE}.id`, `${TAGS_TABLE}.name`, `${TAGS_TABLE}.slug`,
        `${TAGS_TABLE}.description`, `${TAGS_TABLE}.color`, `${TAGS_TABLE}.icon`
      )

    for (const row of rows as (TagRecord & { entity_id: string })[]) {
      const existing = result.get(row.entity_id) ?? []
      existing.push({ id: row.id, name: row.name, slug: row.slug, description: row.description, color: row.color, icon: row.icon })
      result.set(row.entity_id, existing)
    }
    return result
  }

  async function getEntityIdsByTags(entityType: string, slugs: string[], match: 'all' | 'any' = 'all'): Promise<string[]> {
    if (slugs.length === 0) return []
    const tags = await db(TAGS_TABLE).whereIn('slug', slugs).select('id')
    const tagIds = tags.map((t: { id: string }) => t.id)
    if (tagIds.length === 0) return []

    const query = db(ASSIGNMENTS_TABLE)
      .where('entity_type', entityType)
      .whereIn('tag_id', tagIds)
      .groupBy('entity_id')

    if (match === 'all' && tagIds.length > 1) {
      query.havingRaw('COUNT(DISTINCT tag_id) = ?', [tagIds.length])
    }

    return (await query.select('entity_id')).map((r: { entity_id: string }) => r.entity_id)
  }

  async function syncEntityTags(entityType: string, entityId: string, tagIds: string[], auth: { userId?: string; ability?: any }): Promise<TagRecord[]> {
    validateEntityType(entityType)
    await checkEntityAccess(entityType, entityId, auth)
    await syncTags(entityType, entityId, tagIds)
    return getTagsForEntity(entityType, entityId)
  }

  async function assignEntityTag(entityType: string, entityId: string, tagId: string, auth: { userId?: string; ability?: any }): Promise<void> {
    validateEntityType(entityType)
    await checkEntityAccess(entityType, entityId, auth)
    await assign(tagId, entityType, entityId)
  }

  async function unassignEntityTag(entityType: string, entityId: string, tagId: string, auth: { userId?: string; ability?: any }): Promise<void> {
    validateEntityType(entityType)
    await checkEntityAccess(entityType, entityId, auth)
    await unassign(tagId, entityType, entityId)
  }

  return {
    assign,
    unassign,
    syncTags,
    removeAllForEntity,
    getTagsForEntity,
    findBySlug,
    getEntitiesForTag,
    // New methods
    validateEntityType,
    checkEntityAccess,
    getTagsForEntities,
    getEntityIdsByTags,
    syncEntityTags,
    assignEntityTag,
    unassignEntityTag,
  }
}
